import React from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { PHASE_INFO } from '../constants';

const BODY_FONT = 'Rubik-Regular, system-ui, sans-serif';

function minutesPluralKey(count) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'phaseMinutes_one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'phaseMinutes_few';
  return 'phaseMinutes_many';
}

const SectionTag = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1.4,
  color: theme.palette.primary.main,
  textAlign: 'center',
  marginBottom: 8,
}));

const Heading = styled(Typography)({
  fontSize: 28,
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: 24,
  lineHeight: 1.25,
});

const Body = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 15,
  lineHeight: 1.6,
  color: theme.palette.grey[800],
  textAlign: 'center',
  marginBottom: 28,
}));

const Stats = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: 32,
  padding: '20px 0',
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  marginBottom: 28,
}));

const Stat = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
}));

const StatValue = styled(Typography)({
  fontFamily: BODY_FONT,
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1,
});

const StatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1,
  color: theme.palette.grey[500],
}));

const BeginButton = styled(Button)({
  fontFamily: BODY_FONT,
  borderRadius: 25,
  padding: '14px 48px',
  fontSize: 16,
  fontWeight: 600,
  textTransform: 'none',
  display: 'block',
  margin: '0 auto',
});

const Tip = styled('div')(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 14,
  lineHeight: 1.55,
  color: theme.palette.grey[800],
  // Salad-green palette to match the brand; no red/yellow caution chrome.
  backgroundColor: '#EFFAE2',
  border: `1px solid ${theme.palette.primary?.main || '#9FD356'}`,
  borderRadius: 14,
  padding: '14px 16px',
  marginBottom: 28,
  textAlign: 'left',
}));

const TipLabel = styled('span')(({ theme }) => ({
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1,
  fontSize: 11,
  color: theme.palette.primary?.dark || '#4A7A1F',
  display: 'block',
  marginBottom: 4,
}));

export default function PhaseIntro({ phaseIndex, totalPhases, questionType, questionCount, minutes: minutesProp, onBegin }) {
  const { t } = useTranslation();
  const info = PHASE_INFO[questionType];

  const title = info ? t(`Assessment.${info.titleKey}`) : t('Assessment.phaseFallbackTitle');
  const body = info ? t(`Assessment.${info.bodyKey}`) : t('Assessment.phaseFallbackBody');
  // Caller-supplied minutes (e.g. derived from server-timed phases) wins over
  // the static estimate in PHASE_INFO. If neither is present, the minutes
  // stat card is omitted rather than showing a wrong number.
  const minutes = minutesProp ?? info?.minutes ?? null;

  return (
    <>
      <SectionTag>
        {t('Assessment.phaseSectionLabel', { current: phaseIndex + 1, total: totalPhases })}
      </SectionTag>
      <Heading variant="h1">{title}</Heading>

      <Body>{body}</Body>

      {questionType === 'wonderlic' && (
        <Tip>
          <TipLabel>{t('Assessment.phaseTipLabel', { defaultValue: 'Tip' })}</TipLabel>
          {t('Assessment.phaseTipWonderlic', {
            defaultValue:
              'Treat it like a game — don’t get stuck. There are a lot of questions, and answering 25–30 of them correctly already places you in the above-average range. If a question needs too much thinking, skip it and move on.',
          })}
        </Tip>
      )}

      <Stats>
        <Stat>
          <StatValue>{questionCount ?? '—'}</StatValue>
          <StatLabel>{t('Assessment.phaseQuestions')}</StatLabel>
        </Stat>
        {minutes != null && (
          <Stat>
            <StatValue>{minutes}</StatValue>
            <StatLabel>{t(`Assessment.${minutesPluralKey(minutes)}`)}</StatLabel>
          </Stat>
        )}
      </Stats>

      <BeginButton variant="contained" color="primary" size="large" onClick={onBegin}>
        {t('Assessment.phaseBegin')}
      </BeginButton>
    </>
  );
}
